import { useEffect, useState } from "react";
import { Hotel } from "lucide-react";
import { Button, EmptyState, Spinner, Icon, MDisplay } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { RequireBand } from "../../app/RequireBand.js";
import { useMobileBack } from "../../app/mobileBack.js";
import { useShow, useShowMembers } from "../../app/hooks/useShows.js";
import { useTravel, useFlightStatus } from "../../app/hooks/useTravel.js";
import { useSharedShow } from "../../app/hooks/useShowShares.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { initialsFrom } from "../tasks/taskPeople.js";
import { useLiveGroup } from "../../app/realtime/RealtimeProvider.js";
import { useOnlineStatus } from "../../app/hooks/useOnlineStatus.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { FoodFinderCard } from "./FoodFinderCard.js";
import { MyBunkCard } from "../buses/MyBunkCard.js";
import { OfflineBanner } from "./OfflineBanner.js";
import { JourneyStrip } from "./JourneyStrip.js";
import { PlaceActionsSheet } from "./PlaceActionsSheet.js";
import { locationLine, journeyProgress, type PlaceRef } from "./dayOfShow.js";
import styles from "./dayOfShow.module.scss";

type TravelItemDto = components["schemas"]["TravelItemDto"];
type FlightStatusDto = components["schemas"]["FlightStatusDto"];
type MemberDto = components["schemas"]["MemberDto"];

/** A member's flights — assigned to everyone, or naming this person. */
function flightsFor(flights: readonly TravelItemDto[], m: MemberDto): TravelItemDto[] {
  return flights.filter((f) => f.assignedAll || (m.personId != null && (f.personIds ?? []).includes(m.personId)));
}

/**
 * The `/shows/:id/logistics` page — hotel tap-to-maps, member flights with LIVE window-gated
 * status, and food browse for the show. Reads the show record + travel rows + `travel/flight-status` (the
 * server-poller cache; NEVER a client→vendor call). The live in-app `flight-status-changed` SIGNAL is
 * [POST-LAUNCH] (the SkyLink decision), so go-live renders whatever the poller cache holds, refreshed on
 * staleness / foreground resync — not a socket push. Online-first with an honest offline banner.
 */
export function ShowLogisticsScreen({ showId, onBack }: { showId: string; onBack?: () => void }) {
  return (
    <RequireBand>
      <LogisticsInner showId={showId} onBack={onBack} />
    </RequireBand>
  );
}

function LogisticsInner({ showId, onBack }: { showId: string; onBack?: () => void }) {
  useLiveGroup(`show:${showId}`);
  useMobileBack(onBack); // back rides the shell header's leading slot (logo-replacement), not the body
  // The hotel tap-actions sheet (Copy · Open Address · Lyft · Uber) — one at a time (Ryan, ART-174).
  const [place, setPlace] = useState<PlaceRef | null>(null);
  // A pure local 30s tick so the flight strips' planes advance by wall clock (same cadence as the DOS clock).
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  const online = useOnlineStatus();
  const show = useShow(showId);
  // A show this band plays as an accepted GUEST (ART-249) is on the HOST's spine, so the owner read 404s; the
  // header's venue + city then come from the shared-show read. Travel, members, flights and food are the guest
  // band's own (all guest-reachable), so only the header needed a second source.
  const ownMissing = show.error instanceof ApiError && show.error.status === 404;
  const shared = useSharedShow(ownMissing ? showId : undefined);
  const travel = useTravel(showId);
  const members = useShowMembers(showId);
  const flightStatus = useFlightStatus(showId, true);

  const detail: { venueName?: string | null; city?: string | null; state?: string | null } | null =
    show.data?.data ?? (ownMissing && shared.data?.status === "accepted" ? shared.data : null);

  if (show.isLoading || travel.isLoading || (ownMissing && shared.isLoading)) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={22} label="Loading logistics" />
      </div>
    );
  }
  if (!detail) {
    const err = ownMissing ? shared.error : show.error;
    return (
      <div className={styles.screen}>
        {!online && <OfflineBanner />}
        <EmptyState
          title="Couldn't load logistics"
          description={err instanceof ApiError ? err.message : "Check your connection and try again."}
          action={<Button onClick={() => void (ownMissing ? shared.refetch() : show.refetch())}>Retry</Button>}
        />
      </div>
    );
  }

  const items = travel.data?.items ?? [];
  const hotels = items.filter((t) => t.kind === "hotel");
  const flights = items.filter((t) => t.kind === "flight");
  const memberItems = members.data?.items ?? [];
  const statusById = new Map<string, FlightStatusDto>();
  for (const s of flightStatus.data?.flights ?? []) if (s.travelItemId) statusById.set(s.travelItemId, s);

  return (
    <div className={styles.screen} data-testid="logistics">
      {!online && <OfflineBanner />}

      <header className={styles.header}>
        <div className={styles.headerText}>
          <span className={styles.headerEyebrow}>Logistics</span>
          <MDisplay className={styles.headerTitle}>{detail.venueName || "Show logistics"}</MDisplay>
          {locationLine(detail) && <span className={styles.headerSub}>{[detail.city, detail.state].filter(Boolean).join(", ")}</span>}
        </div>
      </header>

      {/* Section order (spec): Bus → Lodging → Members → Food. The bus is Pro-gated + personal — it renders
          nothing for a non-Pro band or an unbunked seat, so the page reads as it did before buses existed. */}
      <MyBunkCard emptyMode="note" />

      <section className={styles.logSection}>
        <span className={styles.sectionEyebrow}>Lodging</span>
        {hotels.length === 0 ? (
          <p className={styles.empty}>No hotel booked.</p>
        ) : (
          <ul className={styles.logList}>
            {hotels.map((h, idx) => (
              <HotelRow key={h.travelItemId ?? idx} hotel={h} onOpen={setPlace} />
            ))}
          </ul>
        )}
      </section>

      <section className={styles.logSection}>
        <span className={styles.sectionEyebrow}>Members</span>
        {flightStatus.isError && (
          <p className={styles.degraded}>Live status is unavailable right now — showing scheduled times.</p>
        )}
        {memberItems.length === 0 ? (
          <p className={styles.empty}>No members on this show.</p>
        ) : (
          memberItems.map((m) => (
            <MemberBlock
              key={m.showMemberId}
              member={m}
              flights={flightsFor(flights, m)}
              statusById={statusById}
            />
          ))
        )}
      </section>

      <FoodFinderCard showId={showId} city={detail.city ?? ""} online={online} />

      {place && <PlaceActionsSheet place={place} onClose={() => setPlace(null)} />}
    </div>
  );
}

/** A lodging row — tapping it opens the tap-actions sheet (Copy · Open Address · Lyft · Uber). The standalone
 *  map-pin button was cut (Ryan, ART-174); the whole row is the tap target now. */
function HotelRow({ hotel, onOpen }: { hotel: TravelItemDto; onOpen: (place: PlaceRef) => void }) {
  const name = hotel.propertyName || "Hotel";
  const stay = [hotel.checkIn, hotel.checkOut].filter(Boolean).join(" → ");
  return (
    <li>
      <button
        type="button"
        className={styles.logRow}
        data-testid="logistics-hotel"
        onClick={() =>
          onOpen({
            name: hotel.propertyName,
            address: hotel.address,
            lat: hotel.lat,
            lng: hotel.lng,
            confirmationCode: hotel.confirmationCode,
          })
        }
        aria-label={`Actions for ${name}`}
      >
        <Icon icon={Hotel} size={20} />
        <div className={styles.logRowBody}>
          <span className={styles.logRowTitle}>{name}</span>
          {hotel.address && <span className={styles.logRowMeta}>{hotel.address}</span>}
          <span className={styles.logRowMeta}>
            {[stay, hotel.room ? `Room ${hotel.room}` : "", hotel.confirmationCode ? `Conf ${hotel.confirmationCode}` : ""]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
      </button>
    </li>
  );
}

/** One member — avatar + name + a phase badge from their soonest flight — followed by that member's live flight
 *  strip(s), or "No flights found" (and no badge) when they have none (spec §Members). */
function MemberBlock({
  member,
  flights,
  statusById,
}: {
  member: MemberDto;
  flights: TravelItemDto[];
  statusById: Map<string, FlightStatusDto>;
}) {
  const name = member.displayName ?? "Member";
  // Legs in departure order so the stitched journey reads first → last.
  const legs = [...flights].sort((a, b) => (a.departLocal ?? "").localeCompare(b.departLocal ?? ""));
  const badge = legs.length > 0 ? phaseBadge(legs, statusById) : null;
  return (
    <div className={styles.memberBlock} data-testid="logistics-member">
      <div className={styles.memberHead}>
        <PersonAvatar personId={member.personId} avatarMediaId={member.avatarMediaId} initials={initialsFrom(name)} size="sm" />
        <span className={styles.memberName}>{name}</span>
        {badge && <span className={styles.phaseBadge} data-phase={badge.phase}>{badge.label}</span>}
      </div>
      {legs.length === 0 ? (
        <p className={styles.logNoFlight}>No flights found</p>
      ) : (
        <JourneyStrip
          legs={legs}
          statusById={statusById}
          fallbackMeta={legs[0]!.assignedAll ? "Everyone" : `${legs[0]!.personIds?.length ?? 0} assigned`}
        />
      )}
    </div>
  );
}

/** The member's phase badge from their whole journey. SCHEDULED (no live data) and UPCOMING (data resolved,
 *  pre-departure) both map to --pre; a LAYOVER shows as --flying; then --flying (In air) and --landed. */
function phaseBadge(legs: TravelItemDto[], statusById: Map<string, FlightStatusDto>): { phase: string; label: string } {
  const { phase } = journeyProgress(legs.map((f) => ({ departIso: f.departLocal, arriveIso: f.arriveLocal })));
  if (phase === "flying") return { phase: "flying", label: "In air" };
  if (phase === "layover") return { phase: "flying", label: "Layover" };
  if (phase === "landed") return { phase: "landed", label: "Landed" };
  const hasStatus = legs.some((f) => f.travelItemId != null && statusById.has(f.travelItemId));
  return { phase: "pre", label: hasStatus ? "Upcoming" : "Scheduled" };
}

